import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FileText, Copy, Trash2, Type } from 'lucide-react';
import toast from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';
import { useSEO } from '../hooks/useSEO';

const CaseConverter: React.FC = () => {
  const [text, setText] = useState('');

  // SEO optimization
  useSEO({
    title: 'Case Converter - Convert Text to Different Cases | SnapTools',
    description: 'Free case converter tool. Convert text to uppercase, lowercase, title case, camelCase, snake_case, kebab-case and more. Instant text case conversion.',
    keywords: 'case converter, text converter, uppercase, lowercase, title case, camelCase, snake_case, kebab-case, text transformation'
  });

  const convertCase = (type: string) => {
    switch (type) {
      case 'upper':
        return text.toUpperCase();
      case 'lower':
        return text.toLowerCase();
      case 'title':
        return text.replace(/\w\S*/g, (txt) => 
          txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
        );
      case 'sentence':
        return text.toLowerCase().replace(/(^\s*\w|[\.\!\?]\s*\w)/g, (c) => 
          c.toUpperCase()
        );
      case 'camel':
        return text.toLowerCase()
          .replace(/[^a-zA-Z0-9]+(.)/g, (_, chr) => chr.toUpperCase())
          .replace(/^./, (m) => m.toLowerCase());
      case 'pascal':
        return text.toLowerCase()
          .replace(/[^a-zA-Z0-9]+(.)/g, (_, chr) => chr.toUpperCase())
          .replace(/^./, (m) => m.toUpperCase());
      case 'snake':
        return text.toLowerCase()
          .replace(/[^a-zA-Z0-9]+/g, '_')
          .replace(/^_+|_+$/g, '');
      case 'kebab':
        return text.toLowerCase()
          .replace(/[^a-zA-Z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');
      case 'alternating':
        return text.split('').map((char, index) => 
          index % 2 === 0 ? char.toLowerCase() : char.toUpperCase()
        ).join('');
      case 'inverse':
        return text.split('').map(char => 
          char === char.toUpperCase() ? char.toLowerCase() : char.toUpperCase()
        ).join('');
      default:
        return text;
    }
  };

  const copyToClipboard = (convertedText: string) => {
    navigator.clipboard.writeText(convertedText).then(() => {
      toast.success('Text copied to clipboard');
    });
  };

  const clearText = () => {
    setText('');
    toast.success('Text cleared successfully');
  };

  const caseTypes = [
    { key: 'upper', label: 'UPPERCASE', description: 'Convert all text to uppercase' },
    { key: 'lower', label: 'lowercase', description: 'Convert all text to lowercase' },
    { key: 'title', label: 'Title Case', description: 'Capitalize the first letter of each word' },
    { key: 'sentence', label: 'Sentence case', description: 'Capitalize first letter of sentences' },
    { key: 'camel', label: 'camelCase', description: 'Convert to camelCase format' },
    { key: 'pascal', label: 'PascalCase', description: 'Convert to PascalCase format' },
    { key: 'snake', label: 'snake_case', description: 'Convert to snake_case format' },
    { key: 'kebab', label: 'kebab-case', description: 'Convert to kebab-case format' },
    { key: 'alternating', label: 'aLtErNaTiNg', description: 'Alternate between upper and lower case' },
    { key: 'inverse', label: 'iNVERSE cASE', description: 'Invert the case of each character' }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-6 sm:py-12">
      <div className="max-w-6xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8 sm:mb-12"
        >
          <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r from-[#8438ff] to-[#7228ee] rounded-xl sm:rounded-2xl mb-4 sm:mb-6">
            <Type className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
          </div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#8438ff] mb-3 sm:mb-4 px-2">
            Case Converter
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Convert text to different case formats instantly. Support for uppercase, lowercase, camelCase, snake_case and more.
          </p>
        </motion.div>

        {/* Header Ad */}
        <AdContainer 
          position="header" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
          className="mb-8"
        />

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Input Section */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl shadow-lg border-2 border-gray-100 p-8"
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-[#8438ff]/10 rounded-xl flex items-center justify-center">
                    <FileText className="h-5 w-5 text-[#8438ff]" />
                  </div>
                  <h2 className="text-xl font-bold text-[#8438ff]">Enter Your Text</h2>
                </div>
                <div className="flex space-x-2">
                  {text && (
                    <button
                      onClick={clearText}
                      className="p-2 text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      title="Clear text"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
              
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Type or paste your text here..."
                className="w-full h-60 p-4 border-2 border-gray-200 rounded-xl resize-none focus:ring-2 focus:ring-[#8438ff] focus:border-[#8438ff] transition-colors"
              />
            </motion.div>
          </div>

          {/* Output Section */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl shadow-lg border-2 border-gray-100 p-8"
            >
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-[#8438ff]/10 rounded-xl flex items-center justify-center">
                  <Type className="h-5 w-5 text-[#8438ff]" />
                </div>
                <h2 className="text-xl font-bold text-[#8438ff]">Converted Text</h2>
              </div>
              
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {caseTypes.map((type) => {
                  const convertedText = convertCase(type.key);
                  return (
                    <div key={type.key} className="border-2 border-gray-100 rounded-xl p-4 hover:border-[#8438ff]/20 transition-colors">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h4 className="font-semibold text-gray-900">{type.label}</h4>
                          <p className="text-sm text-gray-500">{type.description}</p>
                        </div>
                        <button
                          onClick={() => copyToClipboard(convertedText)}
                          disabled={!text}
                          className="p-2 bg-[#8438ff] text-white rounded-lg hover:bg-[#7228ee] disabled:bg-gray-300 transition-colors"
                          title="Copy converted text"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg border min-h-[50px] break-words">
                        <span className="text-gray-700 font-mono text-sm">
                          {convertedText || 'Your converted text will appear here...'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          </div>
        </div>

        {/* Footer Ad */}
        <AdContainer 
          position="footer" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
          className="mt-12"
        />
      </div>
    </div>
  );
};

export default CaseConverter;
